// Nadrama <https://nadrama.com>
// Copyright Nadrama Pty Ltd
// All rights reserved.
import { For, Match, Show, Switch, createEffect, createSignal } from "solid-js";
import { createQuery, useQueryClient } from "@tanstack/solid-query";
import { useLocation } from "@tanstack/solid-router";
import {
  acceptInvitation,
  createAccount,
  getInvitation,
  listAccounts,
} from "./api/generated";
import { apiErrorMessage, authSlotHeaders } from "./api/runtime";
import { completeLogin, hasAuthSlot, logout, startLogin } from "./auth";
import { Button, Field, Form, ValidationMessage } from "./components/forms";
import { Heading } from "./components/layout";
import { formatDate } from "./format";
import {
  accountCreateFormSchema,
  accountCreateFormValue,
  invitationAcceptanceFormSchema,
} from "./form-validation";
import { AppLink, useGo } from "./navigation";
import { sameOriginPath } from "./navigation-url";

export function NotFound() {
  const go = useGo();
  return (
    <>
      <Heading
        title="Page not found"
        copy="This route is unavailable or unsupported."
      />
      <Button onClick={() => go("/")}>Go home</Button>
    </>
  );
}
function InvitationPage(p: { id: string }) {
  const go = useGo();
  const invitation = createQuery(() => ({
    queryKey: ["invitation", p.id],
    queryFn: async () => {
      if (!(await hasAuthSlot(0))) {
        return { authRequired: true as const, details: undefined };
      }
      const result = await getInvitation({
        throwOnError: false,
        responseStyle: "fields",
        path: { id: p.id },
        headers: authSlotHeaders(0),
      });
      if (result.response?.status === 401) {
        return { authRequired: true as const, details: undefined };
      }
      if (result.error) throw result.error;
      return { authRequired: false as const, details: result.data };
    },
    retry: false,
  }));
  const details = () => invitation.data?.details;
  const roleName = () =>
    details()?.groupRole === "admin" ? "Admin" : "Member";
  const matchingEmail = () =>
    details()?.email.toLowerCase() === details()?.currentEmail.toLowerCase();
  return (
    <>
      <Heading title="Join Account" />
      <Show when={invitation.isPending}>
        <p>Loading invitation…</p>
      </Show>
      <Show when={invitation.isError}>
        <div role="alert" class="alert alert-error">
          Unable to load this invitation. It may have expired or been revoked.
        </div>
      </Show>
      <Show when={invitation.data?.authRequired}>
        <p class="text-slate-600">
          Sign in to view and accept this invitation.
        </p>
        <Button
          kind="btn-secondary"
          onClick={() => void startLogin(0, `/invite/${p.id}`)}
        >
          Sign in to continue
        </Button>
      </Show>
      <Show when={details()} keyed>
        {(current) => (
          <Form
            schema={invitationAcceptanceFormSchema}
            action={async () => {
              const result = await acceptInvitation({
                throwOnError: true,
                responseStyle: "fields",
                path: { id: p.id },
                headers: authSlotHeaders(0),
              });
              go(`/u0/${result.data.accountId}`);
            }}
            actions={(busy) => (
              <>
                <div>
                  <button
                    class="btn btn-secondary"
                    type="submit"
                    disabled={busy || !matchingEmail()}
                  >
                    {busy ? "Accepting…" : "Accept Invite"}
                  </button>
                </div>
                <p class="border-t border-slate-200 pt-5 text-center text-sm text-slate-500">
                  <span class="block sm:inline">
                    You’re currently logged in as {current.currentEmail}
                  </span>
                  <span class="hidden px-1 sm:inline">|</span>
                  <AppLink
                    class="block pt-3 sm:inline sm:pt-0"
                    to={`/logout?destination=${encodeURIComponent(`/invite/${p.id}`)}`}
                  >
                    Sign out
                  </AppLink>
                </p>
              </>
            )}
          >
            <Show when={!matchingEmail()}>
              <div role="alert" class="alert alert-warning">
                <span>
                  This invitation was sent to <strong>{current.email}</strong>,
                  but you’re signed in as{" "}
                  <strong>{current.currentEmail}</strong>. Sign out and continue
                  with the invited email address.
                </span>
              </div>
            </Show>
            <dl class="invite-details">
              <div>
                <dt>Account Name:</dt>
                <dd>{current.accountName}</dd>
              </div>
              <Show when={current.accountOrganisation}>
                <div>
                  <dt>Account Organisation:</dt>
                  <dd>{current.accountOrganisation}</dd>
                </div>
              </Show>
              <div>
                <dt>Invited Email:</dt>
                <dd>{current.email}</dd>
              </div>
              <div>
                <dt>Group:</dt>
                <dd>{current.groupName}</dd>
              </div>
              <div>
                <dt>Group Role:</dt>
                <dd>{roleName()}</dd>
              </div>
              <div>
                <dt>Invitation Expires:</dt>
                <dd>{formatDate(current.expiresAt)}</dd>
              </div>
            </dl>
            <label class="invite-consent cursor-pointer text-sm leading-6">
              <input
                class="checkbox checkbox-sm mt-1 shrink-0"
                type="checkbox"
                name="consent"
                value="accepted"
                required
              />
              <span>
                I agree to the{" "}
                <a
                  data-document-navigation
                  href="https://nadrama.com/agreement"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Subscription Agreement
                </a>
                ,{" "}
                <a
                  data-document-navigation
                  href="https://nadrama.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Website Terms &amp; Conditions
                </a>{" "}
                and{" "}
                <a
                  data-document-navigation
                  href="https://nadrama.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
                .
              </span>
            </label>
            <ValidationMessage name="consent" />
          </Form>
        )}
      </Show>
    </>
  );
}
export function DefaultPage() {
  const go = useGo();
  const queryClient = useQueryClient();
  const routerLocation = useLocation();
  const route = () => routerLocation().pathname;
  const search = () => routerLocation().searchStr;
  const requestedDestination = () =>
    new URLSearchParams(search()).get("destination");
  const destination = () => {
    const requested = requestedDestination();
    return requested?.startsWith("/") && !requested.startsWith("//")
      ? requested
      : "/";
  };
  const [authBusy, setAuthBusy] = createSignal(false);
  const [authError, setAuthError] = createSignal("");
  const beginLogin = async (target: string, prompt?: "create") => {
    setAuthBusy(true);
    setAuthError("");
    try {
      await startLogin(0, target, prompt);
    } catch (reason) {
      setAuthError(apiErrorMessage(reason));
      setAuthBusy(false);
    }
  };
  const finishLogout = async () => {
    setAuthBusy(true);
    setAuthError("");
    const match = destination().match(/^\/u(\d)(?:\/|$)/);
    const slot = match ? Number(match[1]) : 0;
    try {
      const redirect = await logout(slot);
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] === slot,
      });
      go(sameOriginPath(redirect, location.origin), true);
    } catch (reason) {
      setAuthError(apiErrorMessage(reason));
      setAuthBusy(false);
    }
  };
  const accountsQuery = createQuery(() => ({
    queryKey: [0, "accounts"],
    queryFn: () =>
      listAccounts({
        throwOnError: true,
        responseStyle: "fields",
        headers: authSlotHeaders(0),
      }),
    enabled: route() === "/select",
  }));
  const accounts = () => accountsQuery.data?.data.items ?? [];
  const callbackSlot = () =>
    route() === "/" ? new URLSearchParams(search()).get("callback") : null;
  const beginRouteLogin = () =>
    beginLogin(
      requestedDestination() ?? "/select",
      route() === "/signup" ? "create" : undefined,
    );
  createEffect(() => {
    const rawSlot = callbackSlot();
    if (rawSlot !== null) {
      const slot = Number(rawSlot);
      if (!Number.isInteger(slot) || slot < 0 || slot > 9) {
        setAuthError(
          "The login callback has an invalid authentication slot. Restart sign in.",
        );
        return;
      }
      setAuthBusy(true);
      void completeLogin(slot)
        .then((destination) => go(sameOriginPath(destination, location.origin), true))
        .catch((reason) => {
          setAuthError(apiErrorMessage(reason));
          setAuthBusy(false);
        });
      return;
    }
    if (route() === "/" || route() === "/login" || route() === "/signup") {
      void beginRouteLogin();
      return;
    }
    if (route() === "/select" && accountsQuery.isSuccess) {
      if (accounts().length === 0) void go("/new", true);
      else if (accounts().length === 1)
        void go(`/u0/${accounts()[0]!.id}`, true);
    }
  });
  return (
    <Switch
      fallback={
        <>
          <Heading
            title="Nadrama Console"
            copy="Securely manage your cloud infrastructure."
          />
          <Button
            onClick={() => void beginLogin("/select")}
            disabled={authBusy()}
          >
            Sign in with email
          </Button>
          <Show when={authError()}>
            <div role="alert" class="alert alert-error mt-4">
              {authError()}{" "}
              <button
                class="btn btn-sm"
                type="button"
                onClick={() => void beginLogin("/select")}
              >
                Restart sign in
              </button>
            </div>
          </Show>
        </>
      }
    >
      <Match
        when={
          (route() === "/" && callbackSlot() === null) ||
          route() === "/login" ||
          route() === "/signup"
        }
      >
        <Show
          when={authError()}
          fallback={
            <p>
              {route() === "/signup"
                ? "Redirecting you to sign up…"
                : "Redirecting you to sign in…"}
            </p>
          }
        >
          <div role="alert" class="alert alert-error">
            <span>{authError()}</span>
            <button
              class="btn btn-sm shrink-0 whitespace-nowrap"
              type="button"
              disabled={authBusy()}
              onClick={() => void beginRouteLogin()}
            >
              {authBusy() ? "Trying…" : "Try again"}
            </button>
          </div>
        </Show>
      </Match>
      <Match when={callbackSlot() !== null}>
        <Heading title="Completing sign in" />
        <Show when={!authError()}>
          <p>Verifying this browser…</p>
        </Show>
        <Show when={authError()}>
          <div role="alert" class="alert alert-error">
            {authError()}
          </div>
          <Button onClick={() => void beginLogin("/select")}>
            Restart sign in
          </Button>
        </Show>
      </Match>
      <Match when={route() === "/logout"}>
        <Heading title="Sign out" />
        <p>Are you sure you want to log out?</p>
        <div class="mt-6 flex flex-wrap gap-3">
          <Button
            kind="btn-secondary"
            disabled={authBusy()}
            onClick={() => void finishLogout()}
          >
            {authBusy() ? "Logging out…" : "Logout"}
          </Button>
          <Button kind="btn-outline" onClick={() => go(destination())}>
            Never mind
          </Button>
        </div>
        <Show when={authError()}>
          <div role="alert" class="alert alert-error mt-4">
            {authError()}
          </div>
        </Show>
      </Match>
      <Match when={route() === "/select"}>
        <Heading title="Select Account..." />
        <Show when={accountsQuery.isError}>
          <div role="alert" class="alert alert-error">
            Unable to load accounts. Please try again.
          </div>
        </Show>
        <Show when={accountsQuery.isPending}>
          <p>Loading accounts…</p>
        </Show>
        <Show when={accountsQuery.isSuccess && accounts().length > 1}>
          <div class="grid gap-3">
            <For each={accounts()}>
              {(a) => (
                <button
                  class="panel flex min-h-16 items-center justify-between p-4 text-left hover:border-primary"
                  onClick={() => go(`/u0/${a.id}`)}
                >
                  <span>
                    <strong>{a.name}</strong>
                    <Show when={a.organisation}>
                      <small class="block text-slate-500">
                        {a.organisation}
                      </small>
                    </Show>
                  </span>
                  <span>Continue →</span>
                </button>
              )}
            </For>
            <Button
              kind="btn-ghost"
              onClick={() => go("/logout?destination=/select")}
            >
              Sign out
            </Button>
          </div>
        </Show>
      </Match>
      <Match when={route() === "/new"}>
        <Heading title="Create an Account" />
        <p>
          Sign up now to join our waitlist and we’ll enable access as soon as
          possible.
        </p>
        <Form
          schema={accountCreateFormSchema}
          value={accountCreateFormValue}
          action={async (d) => {
            const account = await createAccount({
              throwOnError: true,
              responseStyle: "fields",
              headers: authSlotHeaders(0),
              body: {
                userName: d.userName,
                organisation: d.organisation ?? "",
                country: d.country ?? "",
                timezone: d.timezone,
                agreementAccepted: d.agreementAccepted,
              },
            });
            go(`/u0/${account.data.id}`);
          }}
          submitLabel="Create account"
        >
          <input
            type="hidden"
            name="timezone"
            value={Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"}
          />
          <Field
            name="userName"
            label="Your Name"
            placeholder="Your first or preferred name..."
            required
            minLength={1}
            maxLength={120}
          />
          <Field
            name="organisation"
            label="Company/Organisation Name (optional)"
            maxLength={255}
          />
          <Field
            name="country"
            label="Country"
            value="AU"
            required
            minLength={2}
            maxLength={2}
            pattern="[A-Za-z]{2}"
          />
          <label class="invite-consent cursor-pointer text-sm leading-6">
            <input
              class="checkbox checkbox-sm mt-1 shrink-0"
              type="checkbox"
              name="agreementAccepted"
              value="accepted"
              required
            />
            <span>
              I agree to the{" "}
              <a
                data-document-navigation
                href="https://nadrama.com/agreement"
                target="_blank"
                rel="noopener noreferrer"
              >
                Subscription Agreement
              </a>
              ,{" "}
              <a
                data-document-navigation
                href="https://nadrama.com/terms"
                target="_blank"
                rel="noopener noreferrer"
              >
                Website Terms &amp; Conditions
              </a>{" "}
              and{" "}
              <a
                data-document-navigation
                href="https://nadrama.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              .
            </span>
          </label>
          <ValidationMessage name="agreementAccepted" />
        </Form>
      </Match>
      <Match when={route().startsWith("/invite/")}>
        <InvitationPage id={route().split("/")[2] ?? ""} />
      </Match>
      <Match when={route() === "/waitlist"}>
        <Heading
          title="You’re on the waitlist"
          copy="Thanks for your interest. We’ll email you when your account is approved."
        />
      </Match>
    </Switch>
  );
}
